import { useEffect, useState } from 'react'

import { Tooltip as ReactTooltip } from 'react-tooltip'

import { Box } from 'app/components/design-system-components'
import { ErrorBox } from 'app/components/forms/util'
import { kolmeoPMSId } from 'app/constants/pms.constants'
import { activatePMS, deactivatePMS, getPMSConfig } from 'app/services/http/pms'
import * as snugNotifier from 'app/services/snugNotifier'
import * as Form from 'app/shared_components/form_component'

export const KolmeoIntegration = ({
  agencyId,
  integrationsBundleEnabled,
}: any) => {
  const [isEnabled, setIsEnabled] = useState(false)
  const [isActive, setIsActive] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [kolmeoApiKey, setKolmeoApiKey] = useState(null)
  const [apiErr, setApiErr] = useState(null)

  useEffect(() => {
    setApiErr(null)
    getPMSConfig(agencyId, kolmeoPMSId)
      .then((resp: any) => {
        const { apiKey } = resp || {}
        setKolmeoApiKey(apiKey)
        setIsActive(!!apiKey)
        setIsEnabled(!!apiKey)
      })
      .catch(({ message }: any) => {
        snugNotifier.error(message)
        setApiErr(message)
      })
  }, [agencyId])

  const changeIntegrationAvailability = () => {
    setIsEnabled(!isEnabled)
    if (isEnabled) {
      setApiErr(null)
      setKolmeoApiKey(null)
    }
  }

  const activate = () => {
    if (isActive) {
      snugNotifier.error('Kolmeo PMS already activated')
      return
    }

    if (!kolmeoApiKey) {
      snugNotifier.error('Please set api-key')
      return
    }

    setApiErr(null)
    setIsLoading(true)

    activatePMS(agencyId, kolmeoPMSId, { apiKey: kolmeoApiKey })
      .then(() => {
        setIsActive(true)
        snugNotifier.success('Kolmeo has been activated for agency')
      })
      .catch(({ message }: any) => {
        snugNotifier.error(message)
        setApiErr(message)
      })
      .finally(() => setIsLoading(false))
  }

  const deactivate = () => {
    if (!isActive) {
      snugNotifier.error('Kolmeo PMS not activated')
      return
    }

    setApiErr(null)
    setIsLoading(true)

    deactivatePMS(agencyId, kolmeoPMSId)
      .then(() => {
        setKolmeoApiKey(null)
        setIsActive(false)
        setIsEnabled(false)
      })
      .catch(({ message }: any) => {
        snugNotifier.error(message)
        setApiErr(message)
      })
      .finally(() => setIsLoading(false))
  }

  const maskedAPIKey =
    isActive && kolmeoApiKey
      ? `${(kolmeoApiKey as string).slice(0, 24)}xxxxxxxxxxxx`
      : kolmeoApiKey

  const updateAPIKey = ({ target: { value } }: any) => {
    setKolmeoApiKey(value)
  }

  return (
    <Box mb={4} className={'col-sm-12'} pl={0}>
      <Form.Checkbox
        id="kolmeoEnabled"
        label={`Enable ${kolmeoPMSId}`}
        checked={isEnabled}
        onChange={changeIntegrationAvailability}
        readOnly={isActive || !integrationsBundleEnabled}
        toolTip={
          <div>
            <i
              className="icon-help-outline fs16 gray-light-color ibm"
              data-tooltip-content=""
              data-tooltip-id="kolmeoIntegration"
            />
          </div>
        }
      />

      <Box>
        <ReactTooltip id="kolmeoIntegration">
          <span>
            Kolmeo Supplier source must be configured before activating,
            Integration bundle must be enabled to be able to send tenancy
            onboarding
          </span>
        </ReactTooltip>
      </Box>

      {isEnabled && (
        <>
          <div className="alert alert-danger">
            Agency API Key from {kolmeoPMSId}
          </div>
          <div className="input-box">
            <input
              disabled={isActive}
              type="text"
              required
              value={maskedAPIKey as string}
              onChange={updateAPIKey}
            />
          </div>
          <Box mb={4}>
            <ErrorBox error={apiErr} showError={!!apiErr} />
          </Box>

          <Form.Button
            className="mb10 mr10"
            onClick={isActive ? deactivate : activate}
            disabled={!isActive && !kolmeoApiKey}
            label={isActive ? 'Deactivate' : 'Activate'}
            loading={isLoading}
          />
        </>
      )}
    </Box>
  )
}
